import { useState, useCallback } from 'react';
import { Loader2, Car, Edit, ExternalLink } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, InputDecimal } from '../ui';
import { useApiEffect } from '../../hooks';
import type { EntradaVeiculo, EntradaVeiculoCreate, TipoEntrada } from '../../types';
import { formatCurrency, formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS_ENTRADA: { value: TipoEntrada; label: string }[] = [
  { value: 'COMPRA', label: 'Compra' },
  { value: 'CONSIGNACAO', label: 'Consignação' },
  { value: 'TROCA', label: 'Troca' },
  { value: 'LEILAO', label: 'Leilão' },
  { value: 'OUTROS', label: 'Outros' },
];

const emptyForm: EntradaVeiculoCreate = {
  tipoEntrada: 'COMPRA',
  dataEntrada: new Date().toISOString().split('T')[0],
  valorCompra: undefined,
  valorAvaliacao: undefined,
  fornecedorId: undefined,
  localOrigem: '',
  notaFiscalNumero: '',
  notaFiscalUrl: '',
  comissaoValor: undefined,
  observacoes: '',
};

export function EntradaVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showApiError } = useNotification();

  const [entrada, setEntrada] = useState<EntradaVeiculo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<EntradaVeiculoCreate>(emptyForm);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const response = await api.get<EntradaVeiculo>(`/veiculos/${veiculoId}/entrada`, { signal });
      setEntrada(response.data);
      if (response.data) {
        setForm({
          tipoEntrada: response.data.tipoEntrada,
          dataEntrada: response.data.dataEntrada.split('T')[0],
          valorCompra: response.data.valorCompra,
          valorAvaliacao: response.data.valorAvaliacao,
          fornecedorId: response.data.fornecedorId,
          localOrigem: response.data.localOrigem || '',
          notaFiscalNumero: response.data.notaFiscalNumero || '',
          notaFiscalUrl: response.data.notaFiscalUrl || '',
          comissaoValor: response.data.comissaoValor,
          observacoes: response.data.observacoes || '',
        });
      }
      setIsEditing(false);
    } catch {
      // Se não existir entrada, mostra formulário vazio
      setEntrada(null);
      setIsEditing(true);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId, showErrorNotification: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      if (entrada) {
        await api.put(`/veiculos/${veiculoId}/entrada/${entrada.id}`, form);
        showSuccess('Dados de entrada atualizados');
      } else {
        await api.post(`/veiculos/${veiculoId}/entrada`, form);
        showSuccess('Dados de entrada cadastrados');
      }
      setIsEditing(false);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTipoEntradaLabel = (tipo: TipoEntrada) => {
    return TIPOS_ENTRADA.find((t) => t.value === tipo)?.label || tipo;
  };

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Entrada - ${veiculoNome}`} size="lg">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Entrada - ${veiculoNome}`} size="lg">
      <div className="space-y-4">
        {!isEditing && entrada ? (
          // Visualização
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Tipo de Entrada</p>
                <p className="font-semibold text-gray-900">{getTipoEntradaLabel(entrada.tipoEntrada)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Data de Entrada</p>
                <p className="font-semibold text-gray-900">{formatDate(entrada.dataEntrada)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {entrada.valorCompra && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Valor de Compra</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(entrada.valorCompra)}</p>
                </div>
              )}
              {entrada.valorAvaliacao && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Valor de Avaliação</p>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(entrada.valorAvaliacao)}</p>
                </div>
              )}
            </div>

            {entrada.fornecedorNome && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Fornecedor</p>
                <p className="font-medium text-gray-900">{entrada.fornecedorNome}</p>
              </div>
            )}

            {entrada.localOrigem && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Local de Origem</p>
                <p className="font-medium text-gray-900">{entrada.localOrigem}</p>
              </div>
            )}

            {entrada.notaFiscalNumero && (
              <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Nota Fiscal</p>
                  <p className="font-medium text-gray-900">{entrada.notaFiscalNumero}</p>
                </div>
                {entrada.notaFiscalUrl && (
                  <a
                    href={entrada.notaFiscalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {entrada.comissaoValor && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-600">Comissão</p>
                    <p className="font-bold text-yellow-700">{formatCurrency(entrada.comissaoValor)}</p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded font-medium ${
                      entrada.comissaoPaga ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {entrada.comissaoPaga ? 'Paga' : 'Pendente'}
                  </span>
                </div>
              </div>
            )}

            {entrada.observacoes && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Observações</p>
                <p className="text-gray-700 whitespace-pre-wrap">{entrada.observacoes}</p>
              </div>
            )}

            <div className="text-xs text-gray-400 pt-4 border-t">
              Cadastrado em {formatDate(entrada.dataCadastro)}
              {entrada.usuarioNome && ` por ${entrada.usuarioNome}`}
            </div>
          </div>
        ) : (
          // Formulário
          <form onSubmit={handleSubmit} className="space-y-4">
            {!entrada && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Car className="w-6 h-6 text-blue-600" />
                <p className="text-blue-700">Cadastre os dados de entrada deste veículo</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Entrada</label>
                <select
                  value={form.tipoEntrada}
                  onChange={(e) => setForm({ ...form, tipoEntrada: e.target.value as TipoEntrada })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS_ENTRADA.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Entrada</label>
                <input
                  type="date"
                  value={form.dataEntrada}
                  onChange={(e) => setForm({ ...form, dataEntrada: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Compra</label>
                <InputDecimal
                  value={form.valorCompra}
                  onChange={(value) => setForm({ ...form, valorCompra: value ?? undefined })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Avaliação</label>
                <InputDecimal
                  value={form.valorAvaliacao}
                  onChange={(value) => setForm({ ...form, valorAvaliacao: value ?? undefined })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Local de Origem</label>
              <input
                type="text"
                value={form.localOrigem}
                onChange={(e) => setForm({ ...form, localOrigem: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Leilão X, Cliente Y, etc."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número Nota Fiscal</label>
                <input
                  type="text"
                  value={form.notaFiscalNumero}
                  onChange={(e) => setForm({ ...form, notaFiscalNumero: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Nota Fiscal</label>
                <input
                  type="url"
                  value={form.notaFiscalUrl}
                  onChange={(e) => setForm({ ...form, notaFiscalUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor Comissão</label>
              <InputDecimal
                value={form.comissaoValor}
                onChange={(value) => setForm({ ...form, comissaoValor: value ?? undefined })}
                prefix="R$ "
                placeholder="0,00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              {entrada && (
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
